import { useMatches } from '@tanstack/react-router'
import { useCallback, useEffect, useRef } from 'react'
import { usePageContext } from './context'
import type { PageAction, PageTabsConfig } from './types'

function isComponentAction(action: PageAction): action is Extract<PageAction, { type: 'component' }> {
	return action.type === 'component'
}

export function usePageHeaderActions() {
	const { actions, setPageActions, updatePageAction, clearPageActions } = usePageContext()
	// The rendered leaf, not the location, which names the next page while this one is still mounted.
	// Keyed on it, `setActions` changes on a tab change, so pages re-register with that tab's handlers.
	const pathname = useMatches({ select: matches => matches.at(-1)?.pathname ?? null })

	const setActions = useCallback(
		(newActions: PageAction[]) => {
			const invalidActions = newActions.filter(action => {
				if (!action.id) {
					return true
				}

				if (isComponentAction(action)) {
					return !action.component
				}

				if (typeof action.onClick !== 'function') {
					return true
				}

				// A button with no label is allowed, but only when an icon or tooltip names it.
				if (!action.label || action.label.trim() === '') {
					const hasIconOrTooltip = !!action.icon || !!(action as Extract<PageAction, { tooltip?: string }>).tooltip
					return !hasIconOrTooltip
				}

				return false
			})

			if (invalidActions.length > 0) {
				console.error('Invalid actions found:', invalidActions)
			}

			setPageActions(newActions, pathname)
		},
		[setPageActions, pathname],
	)

	const setActionLoading = useCallback(
		(actionId: string, loading: boolean) => {
			updatePageAction(actionId, { loading })
		},
		[updatePageAction],
	)

	return {
		actions,
		setActions,
		clearPageActions,
		setActionLoading,
	}
}

export function usePageHeaderTabs() {
	const { dynamicTabs, setPageTabs, clearPageTabs } = usePageContext()
	const registeredRef = useRef<PageTabsConfig | null>(null)
	const routeId = useMatches({ select: matches => matches[matches.length - 1]?.routeId })
	const routeIdRef = useRef(routeId)

	const setTabs = useCallback(
		(tabs: PageTabsConfig | null) => {
			registeredRef.current = tabs
			setPageTabs(tabs)
		},
		[setPageTabs],
	)

	const clearTabs = useCallback(() => {
		registeredRef.current = null
		clearPageTabs()
	}, [clearPageTabs])

	// The header clears what a route change left behind, keyed on the leaf route. A layout that owns
	// tabs and renders an outlet outlives that leaf, and its own effect does not re-run when only the
	// leaf changed, so its tabs would be gone for the rest of the visit. Re-assert what this owner
	// registered, and only on a leaf change: an org switch keeps the same leaf while the page stays
	// mounted, and re-asserting there would put the previous org's tabs back.
	useEffect(() => {
		const leafChanged = routeIdRef.current !== routeId
		routeIdRef.current = routeId
		if (leafChanged && registeredRef.current !== null) {
			setPageTabs(registeredRef.current)
		}
	}, [routeId, setPageTabs])

	return {
		tabs: dynamicTabs,
		setTabs,
		clearTabs,
	}
}

export function usePageHeaderBackButton() {
	const { backButton, setBackButton, clearBackButton } = usePageContext()

	return {
		backButton,
		setBackButton,
		clearBackButton,
	}
}

/**
 * Opts a page into rendering its own controls in the header's action row, for pages that have no CTAs there.
 * Returns the slot to portal into, or null while the header has yet to mount it — so a caller can hold its
 * controls back for that one render instead of mounting them inline and remounting them into the portal.
 *
 * Off unless a page asks for it: pass `false` (the default) and this stays null, leaving the page to lay its
 * controls out itself. The header hides the slot whenever nothing is portalled in, so pages that never call
 * this are untouched.
 */
export function usePageHeaderControls(enabled = false): HTMLElement | null {
	const { headerControlsSlot } = usePageContext()

	return enabled ? (headerControlsSlot ?? null) : null
}

/**
 * Opts a page into rendering the rows it would stack above its content inside the toolbar strip, so
 * collapsing the strip clears them too. Returns the slot to portal into, or null when there is none
 * (mobile, or before the strip has mounted it) and the page should lay the rows out itself.
 *
 * Off unless a page asks for it, the same way `usePageHeaderControls` is.
 */
export function usePageToolbarExtras(enabled = false): HTMLElement | null {
	const { toolbarExtrasSlot } = usePageContext()

	return enabled ? (toolbarExtrasSlot ?? null) : null
}

export function usePageHeaderOverflowTabs() {
	const { hideOverflowTabs, setHideOverflowTabs } = usePageContext()

	return {
		hideOverflowTabs,
		setHideOverflowTabs,
	}
}
